import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MoreHorizontal, Plus, Search, X, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import {
  useWorkspaceTasks,
  type TaskFilter,
  type TaskSort,
  type WorkspaceTask,
} from "@/hooks/useWorkspaceTasks";
import { useWorkspaceProjects, useProjectColumns } from "@/hooks/useWorkspaceProjects";
import { useOrphanTasks } from "@/hooks/useOrphanTasks";
import { useProjectKanbanTasks } from "@/hooks/useProjectKanbanTasks";
import { notifyTasksChanged } from "@/hooks/usePendingTasksCount";
import { useTaskDashboard } from "@/contexts/TaskDashboardContext";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { useToast } from "@/hooks/use-toast";
import { getLastProjectId, setLastProjectId } from "@/lib/tasks-project-storage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { TaskList } from "@/components/tasks/TaskList";
import { MySubtasksSection } from "@/components/tasks/MySubtasksSection";
import { TaskQuickAdd } from "@/components/tasks/TaskQuickAdd";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { TaskMetricsPanel } from "@/components/tasks/TaskMetricsPanel";
import { TaskDayProgressHeader } from "@/components/tasks/TaskDayProgressHeader";
import { TaskReminderBanners } from "@/components/tasks/TaskReminderBanners";
import { TaskDeletionRequestsPanel } from "@/components/tasks/TaskDeletionRequestsPanel";
import { TasksProjectBar } from "@/components/tasks/TasksProjectBar";
import { OrphanTasksBanner } from "@/components/tasks/OrphanTasksBanner";
import { TasksInboxPanel } from "@/components/tasks/TasksInboxPanel";
import { TasksKanban } from "@/components/tasks/TasksKanban";
import { TasksMobileAgenda } from "@/components/tasks/TasksMobileAgenda";
import { ProjectManagerDialog } from "@/components/tasks/ProjectManagerDialog";
import { ProjectColumnManagerDialog } from "@/components/tasks/ProjectColumnManagerDialog";
import { TaskCompleteDialog } from "@/components/tasks/TaskCompleteDialog";
import { TaskDetailSheet } from "@/components/tasks/TaskDetailSheet";
import { TaskCancelDialog, type TaskCancelMode } from "@/components/tasks/TaskCancelDialog";
import { useTaskDeletionRequests } from "@/hooks/useTaskDeletionRequests";
import {
  allSubtasksCompleted,
  COMPLETE_WITH_PENDING_SUBTASKS_MESSAGE,
} from "@/lib/task-date-validation";
import { getSubtaskProgress } from "@/hooks/useWorkspaceTasks";
import { TaskPriorityFilterButton } from "@/components/tasks/TaskPriorityFilterButton";
import {
  filterTasksByPriority,
  type TaskPriority,
} from "@/lib/task-priority";

const REALTIME_DEBOUNCE_MS = 3000;
const SEARCH_DEBOUNCE_MS = 350;

type TasksTab = "minhas" | "equipe" | "metrics";

const FILTERS: { value: TaskFilter; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "today", label: "Hoje" },
  { value: "overdue", label: "Atrasadas" },
  { value: "upcoming", label: "Futuras" },
  { value: "completed", label: "Concluídas" },
];

const SORT_OPTIONS: { value: TaskSort; label: string }[] = [
  { value: "due_date", label: "Prazo" },
  { value: "updated_at", label: "Atualização" },
  { value: "title", label: "Título" },
  { value: "priority", label: "Prioridade" },
];

const VALID_FILTERS = new Set<TaskFilter>([
  "all",
  "today",
  "overdue",
  "upcoming",
  "completed",
]);
const VALID_SORTS = new Set<TaskSort>(["due_date", "updated_at", "title", "priority"]);
const VALID_PRIORITIES = new Set<TaskPriority>(["low", "medium", "high", "urgent"]);

function parseTab(value: string | null): TasksTab {
  if (value === "equipe" || value === "metrics") return value;
  return "minhas";
}

function parseFilter(value: string | null, fallback: TaskFilter): TaskFilter {
  if (value && VALID_FILTERS.has(value as TaskFilter)) return value as TaskFilter;
  return fallback;
}

function parseSort(value: string | null): TaskSort {
  if (value && VALID_SORTS.has(value as TaskSort)) return value as TaskSort;
  return "due_date";
}

function parsePriorities(value: string | null): TaskPriority[] {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter((item): item is TaskPriority => VALID_PRIORITIES.has(item as TaskPriority));
}

interface WorkspaceMemberOption {
  id: string;
  display_name: string | null;
}

export default function Tasks() {
  const { user } = useAuth();
  const { workspaceId, loading: workspaceLoading } = useWorkspace(user?.id);
  const { memberId, isAdmin, canViewTaskPerformance, loading: permsLoading } = useMemberPermissions();
  const { refetch: refetchDashboard, dayProgress } = useTaskDashboard();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState<TasksTab>(() => parseTab(searchParams.get("tab")));
  const [filterMinhas, setFilterMinhas] = useState<TaskFilter>(() =>
    parseFilter(searchParams.get("filter"), "all"),
  );
  const [filterEquipe, setFilterEquipe] = useState<TaskFilter>(() =>
    parseFilter(searchParams.get("filter"), "all"),
  );
  const [priorityFilterMinhas, setPriorityFilterMinhas] = useState<TaskPriority[]>(() =>
    parsePriorities(searchParams.get("priorities")),
  );
  const [priorityFilterEquipe, setPriorityFilterEquipe] = useState<TaskPriority[]>(() =>
    parsePriorities(searchParams.get("priorities")),
  );
  const [sort, setSort] = useState<TaskSort>(() => parseSort(searchParams.get("sort")));
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("q") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get("q")?.trim() ?? "");
  const [assigneeFilter, setAssigneeFilter] = useState<string | null>(() =>
    searchParams.get("assignee"),
  );
  const [members, setMembers] = useState<WorkspaceMemberOption[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDefaultColumnId, setDialogDefaultColumnId] = useState<string | null>(null);
  const [projectManagerOpen, setProjectManagerOpen] = useState(false);
  const [columnManagerOpen, setColumnManagerOpen] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [projectInitDone, setProjectInitDone] = useState(false);
  const [kanbanCompleteTarget, setKanbanCompleteTarget] = useState<WorkspaceTask | null>(null);
  const [kanbanDetailTarget, setKanbanDetailTarget] = useState<WorkspaceTask | null>(null);
  const [kanbanCancelTarget, setKanbanCancelTarget] = useState<WorkspaceTask | null>(null);
  const [mobileSurface, setMobileSurface] = useState<"quadro" | "agenda">("quadro");

  const {
    projects,
    loading: projectsLoading,
    createProject,
    updateProject,
    archiveProject,
  } = useWorkspaceProjects(workspaceId);

  const { columns, loading: columnsLoading, createColumn, updateColumn, deleteColumn } =
    useProjectColumns(activeProjectId);

  const {
    pending: deletionRequests,
    pendingCount: deletionRequestsCount,
    approve: approveDeletion,
    reject: rejectDeletion,
    refetch: refetchDeletionRequests,
  } = useTaskDeletionRequests({ workspaceId, isManager: isAdmin });

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeFilter = tab === "equipe" ? filterEquipe : filterMinhas;
  const activePriorityFilter = tab === "equipe" ? priorityFilterEquipe : priorityFilterMinhas;
  const onlyMine = tab === "minhas";
  const effectiveAssignee =
    isAdmin && tab === "equipe" && !onlyMine ? assigneeFilter : null;
  const isInboxView = activeProjectId === null;
  const isTemporalFilter = activeFilter !== "all";
  const showKanban = !isInboxView && !isTemporalFilter && Boolean(activeProjectId);
  const orphanScopeAll = canViewTaskPerformance && tab === "equipe";

  const { orphanCount, orphans, loading: orphansLoading, refetch: refetchOrphans } =
    useOrphanTasks({
      workspaceId,
      memberId,
      isAdmin: orphanScopeAll,
      onlyMine: !orphanScopeAll,
      searchQuery: debouncedSearch,
      enabled: Boolean(workspaceId),
    });

  const {
    tasks,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refetch,
    createTask,
    duplicateTask,
    completeTask,
    reopenTask,
    cancelTask,
    updateTaskDueDate,
    updateTask,
  } = useWorkspaceTasks({
    workspaceId,
    memberId,
    isAdmin,
    filter: activeFilter,
    onlyMine,
    searchQuery: debouncedSearch,
    assigneeId: effectiveAssignee,
    sort,
    projectId: isInboxView ? null : activeProjectId,
    orphanOnly: isInboxView,
    priorities: activePriorityFilter,
  });

  const {
    tasksByColumn,
    loading: kanbanLoading,
    refetch: refetchKanban,
    optimisticBatchReorder,
    persistKanbanReorder,
  } = useProjectKanbanTasks({
    workspaceId,
    projectId: activeProjectId,
    columns,
    memberId,
    isAdmin,
    onlyMine,
    searchQuery: debouncedSearch,
    enabled: Boolean(activeProjectId && !isInboxView),
  });

  const filteredTasksByColumn = useMemo(() => {
    if (activePriorityFilter.length === 0) return tasksByColumn;
    const filtered = new Map<string, WorkspaceTask[]>();
    for (const [columnId, columnTasks] of tasksByColumn) {
      filtered.set(columnId, filterTasksByPriority(columnTasks, activePriorityFilter));
    }
    return filtered;
  }, [tasksByColumn, activePriorityFilter]);

  const filteredOrphans = useMemo(
    () => filterTasksByPriority(orphans, activePriorityFilter),
    [orphans, activePriorityFilter],
  );

  const agendaTasks = useMemo(() => {
    const list: WorkspaceTask[] = [];
    for (const [, columnTasks] of filteredTasksByColumn) list.push(...columnTasks);
    return list;
  }, [filteredTasksByColumn]);

  useEffect(() => {
    if (projectsLoading || projectInitDone) return;

    const saved = getLastProjectId();
    const savedValid = saved && projects.some((p) => p.id === saved);

    if (savedValid) {
      setActiveProjectId(saved);
    } else if (orphanCount > 0) {
      setActiveProjectId(null);
    } else if (projects.length > 0) {
      setActiveProjectId(projects[0].id);
    } else {
      setActiveProjectId(null);
    }
    setProjectInitDone(true);
  }, [projectsLoading, projects, orphanCount, projectInitDone]);

  useEffect(() => {
    if (!projectInitDone || projectsLoading) return;
    if (activeProjectId !== null || orphanCount > 0) return;

    const saved = getLastProjectId();
    const savedValid = saved && projects.some((p) => p.id === saved);

    if (savedValid) {
      setActiveProjectId(saved);
    } else if (projects.length > 0) {
      setActiveProjectId(projects[0].id);
      setLastProjectId(projects[0].id);
    }
  }, [projectInitDone, projectsLoading, activeProjectId, orphanCount, projects]);

  const handleSelectProject = useCallback((id: string | null) => {
    setActiveProjectId(id);
    setLastProjectId(id);
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (!canViewTaskPerformance && (tab === "equipe" || tab === "metrics")) {
      setTab("minhas");
    }
  }, [canViewTaskPerformance, tab]);

  useEffect(() => {
    if (!isAdmin) setAssigneeFilter(null);
  }, [isAdmin]);

  useEffect(() => {
    if (!canViewTaskPerformance || !workspaceId) return;
    void (async () => {
      const { data } = await supabase
        .from("workspace_members")
        .select("id, display_name")
        .eq("workspace_id", workspaceId)
        .order("display_name", { ascending: true });
      setMembers((data ?? []) as WorkspaceMemberOption[]);
    })();
  }, [canViewTaskPerformance, workspaceId]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (tab !== "minhas") params.set("tab", tab);
    const defaultFilter: TaskFilter = "all";
    if (activeFilter !== defaultFilter) params.set("filter", activeFilter);
    if (sort !== "due_date") params.set("sort", sort);
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (canViewTaskPerformance && tab === "equipe" && assigneeFilter) {
      params.set("assignee", assigneeFilter);
    }
    if (activePriorityFilter.length > 0) {
      params.set("priorities", activePriorityFilter.join(","));
    }
    setSearchParams(params, { replace: true });
  }, [
    tab,
    activeFilter,
    activePriorityFilter,
    sort,
    debouncedSearch,
    assigneeFilter,
    canViewTaskPerformance,
    setSearchParams,
  ]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const realtimeRefs = useRef({ refetch, refetchOrphans, refetchKanban, toast, memberId });
  realtimeRefs.current = { refetch, refetchOrphans, refetchKanban, toast, memberId };

  useEffect(() => {
    if (!workspaceId) return;
    const channelName = `tasks-${workspaceId}`;
    const channel = subscribePostgresChannel(
      channelName,
      {
        event: "*",
        schema: "public",
        table: "workspace_tasks",
        filter: `workspace_id=eq.${workspaceId}`,
      },
      (payload) => {
        const { refetch: r, refetchOrphans: ro, refetchKanban: rk, toast: t, memberId: mid } =
          realtimeRefs.current;
        if (payload.eventType === "UPDATE" && mid) {
          const newRow = payload.new as { assigned_member_id?: string; title?: string };
          const oldRow = payload.old as { assigned_member_id?: string };
          if (newRow.assigned_member_id === mid && oldRow.assigned_member_id !== mid) {
            t({ title: `Nova tarefa atribuída: ${newRow.title ?? "Sem título"}` });
          }
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          void r();
          void ro();
          void rk();
          notifyTasksChanged();
        }, REALTIME_DEBOUNCE_MS);
      },
    );
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [workspaceId]);

  const handleCreate = async (input: Parameters<typeof createTask>[0]) => {
    await createTask(input);
    notifyTasksChanged();
    await refetchKanban();
    void refetchOrphans();
  };

  const handleComplete = async (id: string, outcome: "positive" | "negative") => {
    await completeTask(id, outcome);
    notifyTasksChanged();
    await refetchDashboard();
    await refetchKanban();
    const done = dayProgress.done + 1;
    const total = dayProgress.total;
    toast({
      title: "Tarefa concluída",
      description:
        total > 0 ? `+1 na missão (${Math.min(done, total)}/${total})` : "Bom trabalho!",
    });
  };

  const handleReopen = async (id: string) => {
    await reopenTask(id);
    notifyTasksChanged();
    await refetchKanban();
  };

  const handleCancel = async (id: string, reason?: string) => {
    const result = await cancelTask(id, reason);
    notifyTasksChanged();
    await refetchDashboard();
    await refetchKanban();
    void refetchOrphans();
    return result;
  };

  const handleUpdateDueDate = async (input: {
    taskId: string;
    due_date: string | null;
    due_at: string | null;
    allowPastDue?: boolean;
  }) => {
    await updateTaskDueDate(input);
    notifyTasksChanged();
    await refetchDashboard();
    await refetchKanban();
  };

  const handleDuplicate = async (task: WorkspaceTask) => {
    try {
      await duplicateTask(task);
      notifyTasksChanged();
      toast({ title: "Tarefa duplicada" });
    } catch (err: unknown) {
      toast({
        title: "Erro ao duplicar tarefa",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  const handleUpdateTask = async (input: Parameters<typeof updateTask>[0]) => {
    await updateTask(input);
    notifyTasksChanged();
    await refetchDashboard();
    await refetchKanban();
    void refetchOrphans();
  };

  const requestKanbanComplete = (task: WorkspaceTask) => {
    if (!allSubtasksCompleted(getSubtaskProgress(task))) {
      toast({
        title: "Subtarefas pendentes",
        description: COMPLETE_WITH_PENDING_SUBTASKS_MESSAGE,
        variant: "destructive",
      });
      return;
    }
    setKanbanCompleteTarget(task);
  };

  const kanbanCancelModeFor = (task: WorkspaceTask): TaskCancelMode => {
    if (isAdmin) return "cancel";
    if (task.created_by_member_id === memberId) return "cancel";
    return "request";
  };

  const handleConfirmKanbanCancel = async (reason?: string) => {
    if (!kanbanCancelTarget) return;
    try {
      const result = await handleCancel(kanbanCancelTarget.id, reason);
      if (result === "requested") {
        toast({
          title: "Solicitação enviada",
          description: "O gestor irá avaliar a exclusão desta tarefa.",
        });
      } else if (result === "already_requested") {
        toast({
          title: "Solicitação já existe",
          description: "Já há uma solicitação de exclusão pendente para esta tarefa.",
        });
      } else {
        toast({ title: "Tarefa cancelada" });
      }
    } catch (err: unknown) {
      toast({
        title: "Erro ao cancelar tarefa",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  const handleTabChange = useCallback((value: string) => {
    setTab(value as TasksTab);
  }, []);

  const openCreateDialog = useCallback((columnId?: string | null) => {
    setDialogDefaultColumnId(columnId ?? null);
    setDialogOpen(true);
  }, []);

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) setDialogDefaultColumnId(null);
  }, []);

  const activeProject = useMemo(
    () => projects.find((p) => p.id === activeProjectId),
    [projects, activeProjectId],
  );

  const kanbanDetailTask = useMemo(() => {
    if (!kanbanDetailTarget) return null;
    for (const list of filteredTasksByColumn.values()) {
      const found = list.find((t) => t.id === kanbanDetailTarget.id);
      if (found) return found;
    }
    return kanbanDetailTarget;
  }, [kanbanDetailTarget, filteredTasksByColumn]);

  if (permsLoading || workspaceLoading) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-[240px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!workspaceId) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-[240px] px-4 text-center text-sm text-muted-foreground">
        Não foi possível carregar o workspace. Tente recarregar a página.
      </div>
    );
  }

  const listProps = {
    tasks,
    loading,
    isAdmin,
    currentMemberId: memberId,
    workspaceId,
    onComplete: handleComplete,
    onReopen: handleReopen,
    onCancel: handleCancel,
    onUpdateDueDate: handleUpdateDueDate,
    onUpdateTask: handleUpdateTask,
    onDuplicate: handleDuplicate,
    projects,
  };

  const inboxListProps = {
    ...listProps,
    tasks: filteredOrphans,
    loading: orphansLoading,
  };

  const renderTaskContent = () => {
    if (isInboxView) {
      if (tab === "equipe" && canViewTaskPerformance) {
        return <TasksInboxPanel {...inboxListProps} />;
      }
      return <TaskList {...inboxListProps} />;
    }

    if (showKanban) {
      const board = (
        <TasksKanban
          columns={columns}
          columnsLoading={columnsLoading}
          tasksByColumn={filteredTasksByColumn}
          loading={kanbanLoading}
          isAdmin={isAdmin}
          currentMemberId={memberId}
          searchQuery={searchTerm}
          onOptimisticReorder={optimisticBatchReorder}
          onPersistReorder={persistKanbanReorder}
          onComplete={requestKanbanComplete}
          onReopen={(id) => void handleReopen(id)}
          onView={setKanbanDetailTarget}
          onCancelRequest={setKanbanCancelTarget}
          onOpenColumnManager={() => setColumnManagerOpen(true)}
          isAdminCanManageColumns={isAdmin}
          onAddTask={(columnId) => openCreateDialog(columnId)}
          onCreateColumn={(input) => createColumn({ ...input, workspaceId })}
        />
      );
      return (
        <>
          <div className={cn("md:hidden flex-1 min-h-0", mobileSurface !== "agenda" && "hidden")}>
            <TasksMobileAgenda tasks={agendaTasks} onView={setKanbanDetailTarget} />
          </div>
          <div className={cn("flex-1 min-h-0 flex flex-col overflow-hidden", mobileSurface === "agenda" && "hidden md:flex")}>
            {board}
          </div>
        </>
      );
    }

    return (
      <>
        {isTemporalFilter && (
          <p className="text-xs text-muted-foreground">
            Filtros temporais exibem a lista. Selecione &quot;Todas&quot; para ver o Kanban.
          </p>
        )}
        <TaskList {...listProps} />
        <LoadMoreButton hasMore={hasMore} loadingMore={loadingMore} onLoadMore={loadMore} />
      </>
    );
  };

  const pageSubtitle =
    orphanCount > 0
      ? "Organize por projeto. Tarefas sem projeto ficam em Gerais."
      : "Organize por projeto e acompanhe suas entregas.";

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden px-3 sm:px-4 py-2 sm:py-4 gap-2 sm:gap-3">
      <div className="flex items-center gap-2 shrink-0 sm:justify-between">
        <div className="min-w-0 shrink-0">
          <h1 className="text-lg sm:text-2xl font-bold tracking-tight">Tarefas</h1>
          <p className="hidden sm:block text-sm text-muted-foreground">{pageSubtitle}</p>
        </div>
        {tab === "minhas" && (
          <>
            <div className="min-w-0 flex-1 sm:hidden">
              <TaskDayProgressHeader compact />
            </div>
            <div className="hidden sm:block w-auto max-w-xl">
              <TaskDayProgressHeader />
            </div>
          </>
        )}
        <Button size="icon" className="h-9 w-9 shrink-0 sm:hidden" onClick={() => openCreateDialog()} aria-label="Nova tarefa">
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange} className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <TasksProjectBar
          projects={projects}
          activeProjectId={activeProjectId}
          onSelectProject={handleSelectProject}
          onOpenProjectManager={() => setProjectManagerOpen(true)}
          onOpenColumnManager={() => setColumnManagerOpen(true)}
          onNewTask={() => openCreateDialog()}
          projectsLoading={projectsLoading}
          isAdmin={isAdmin}
          orphanCount={orphanCount}
          showColumnManager={Boolean(activeProjectId)}
          canViewTaskPerformance={canViewTaskPerformance}
          deletionRequestsCount={deletionRequestsCount}
          tab={tab}
          onTabChange={handleTabChange}
        />

        <OrphanTasksBanner
          count={orphanCount}
          onGoToInbox={() => handleSelectProject(null)}
          visible={!isInboxView && orphanCount > 0}
          teamScope={orphanScopeAll}
        />

        <TabsContent
          value="minhas"
          className={cn(
            "mt-2 md:mt-4 flex flex-col flex-1 min-h-0 h-full overflow-hidden data-[state=inactive]:hidden",
            !showKanban && "overflow-auto",
          )}
        >
          <div className={cn("shrink-0", showKanban ? "flex flex-col gap-3" : "space-y-4")}>
            <TaskReminderBanners onFilterChange={setFilterMinhas} />
            {!isInboxView && (
              <>
                <TasksMobileControls
                  showKanban={showKanban}
                  mobileSurface={mobileSurface}
                  onMobileSurfaceChange={setMobileSurface}
                  filter={filterMinhas}
                  setFilter={setFilterMinhas}
                  showCounts
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  selectedPriorities={priorityFilterMinhas}
                  onPrioritiesChange={setPriorityFilterMinhas}
                />
                <div className="hidden md:flex items-center gap-2 min-w-0 mb-3">
                  <FilterBar filter={filterMinhas} setFilter={setFilterMinhas} showCounts />
                  <TaskPriorityFilterButton
                    selectedPriorities={priorityFilterMinhas}
                    onChange={setPriorityFilterMinhas}
                  />
                  {showKanban && (
                    <KanbanSearchInput
                      searchTerm={searchTerm}
                      onSearchChange={setSearchTerm}
                      className="w-[220px]"
                    />
                  )}
                </div>
              </>
            )}
            {!showKanban && (
              <TaskToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                sort={sort}
                onSortChange={setSort}
              />
            )}
            {!showKanban && memberId && (
              <TaskQuickAdd memberId={memberId} onCreate={handleCreate} />
            )}
          </div>
          {showKanban ? (
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">{renderTaskContent()}</div>
          ) : (
            <>
              {renderTaskContent()}
              <MySubtasksSection
                workspaceId={workspaceId}
                memberId={memberId}
                filter={filterMinhas}
                onChanged={() => {
                  void refetch();
                  notifyTasksChanged();
                  void refetchDashboard();
                }}
              />
            </>
          )}
        </TabsContent>

        {canViewTaskPerformance && (
          <TabsContent
            value="equipe"
            className={cn(
              "mt-2 md:mt-4 flex flex-col flex-1 min-h-0 h-full overflow-hidden data-[state=inactive]:hidden",
              !showKanban && "overflow-auto",
            )}
          >
            <div className={cn("shrink-0", showKanban ? "flex flex-col gap-3" : "space-y-4")}>
              {isAdmin && (
                <TaskDeletionRequestsPanel
                  requests={deletionRequests}
                  onApprove={approveDeletion}
                  onReject={rejectDeletion}
                  onReviewed={() => {
                    void refetch();
                    void refetchDeletionRequests();
                    notifyTasksChanged();
                  }}
                />
              )}
              {!isInboxView && (
                <p className="hidden md:block text-xs text-muted-foreground">
                  Projeto: {activeProject?.name ?? "—"}. Use &quot;Todas&quot; para o Kanban.
                </p>
              )}
              {!isInboxView && (
                <>
                  <TasksMobileControls
                    showKanban={showKanban}
                    mobileSurface={mobileSurface}
                    onMobileSurfaceChange={setMobileSurface}
                    filter={filterEquipe}
                    setFilter={setFilterEquipe}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    selectedPriorities={priorityFilterEquipe}
                    onPrioritiesChange={setPriorityFilterEquipe}
                  />
                  <div className="hidden md:flex items-center gap-2 min-w-0 mb-3">
                    <FilterBar filter={filterEquipe} setFilter={setFilterEquipe} />
                    <TaskPriorityFilterButton
                      selectedPriorities={priorityFilterEquipe}
                      onChange={setPriorityFilterEquipe}
                    />
                    {showKanban && (
                      <KanbanSearchInput
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        className="w-[220px]"
                      />
                    )}
                  </div>
                </>
              )}
              {!showKanban && (
                <TaskToolbar
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  sort={sort}
                  onSortChange={setSort}
                  assigneeFilter={assigneeFilter}
                  onAssigneeChange={setAssigneeFilter}
                  members={members}
                />
              )}
            </div>
            {showKanban ? (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">{renderTaskContent()}</div>
            ) : (
              renderTaskContent()
            )}
          </TabsContent>
        )}

        {canViewTaskPerformance && (
          <TabsContent
            value="metrics"
            className="mt-4 flex flex-col flex-1 min-h-0 h-full overflow-auto data-[state=inactive]:hidden"
          >
            <TaskMetricsPanel
              workspaceId={workspaceId}
              projectId={activeProjectId}
              projects={projects}
              members={members}
            />
          </TabsContent>
        )}
      </Tabs>

      {memberId && (
        <TaskDialog
          open={dialogOpen}
          onOpenChange={handleDialogOpenChange}
          workspaceId={workspaceId}
          memberId={memberId}
          isAdmin={isAdmin}
          onCreate={handleCreate}
          defaultProjectId={activeProjectId}
          defaultColumnId={dialogDefaultColumnId}
        />
      )}

      <ProjectManagerDialog
        open={projectManagerOpen}
        onOpenChange={setProjectManagerOpen}
        projects={projects}
        onCreate={createProject}
        onUpdate={updateProject}
        onArchive={archiveProject}
      />

      {workspaceId && activeProjectId && (
        <ProjectColumnManagerDialog
          open={columnManagerOpen}
          onOpenChange={setColumnManagerOpen}
          projectName={activeProject?.name}
          columns={columns}
          onCreate={(input) =>
            createColumn({ ...input, workspaceId })
          }
          onUpdate={updateColumn}
          onDelete={deleteColumn}
        />
      )}

      {kanbanCompleteTarget && (
        <TaskCompleteDialog
          open={Boolean(kanbanCompleteTarget)}
          taskTitle={kanbanCompleteTarget.title}
          onCancel={() => setKanbanCompleteTarget(null)}
          onConfirm={async (outcome) => {
            await handleComplete(kanbanCompleteTarget.id, outcome);
            setKanbanCompleteTarget(null);
          }}
        />
      )}

      {memberId && (
        <TaskDetailSheet
          open={kanbanDetailTarget !== null}
          onOpenChange={(open) => {
            if (!open) setKanbanDetailTarget(null);
          }}
          task={kanbanDetailTask}
          workspaceId={workspaceId}
          memberId={memberId}
          isAdmin={isAdmin}
          onSave={handleUpdateTask}
          onComplete={requestKanbanComplete}
        />
      )}

      <TaskCancelDialog
        open={kanbanCancelTarget !== null}
        mode={kanbanCancelTarget ? kanbanCancelModeFor(kanbanCancelTarget) : "cancel"}
        taskTitle={kanbanCancelTarget?.title ?? ""}
        hasSubtasks={kanbanCancelTarget ? getSubtaskProgress(kanbanCancelTarget).total > 0 : false}
        onOpenChange={(open) => {
          if (!open) setKanbanCancelTarget(null);
        }}
        onConfirm={handleConfirmKanbanCancel}
      />
    </div>
  );
}

function TasksMobileControls({
  showKanban,
  mobileSurface,
  onMobileSurfaceChange,
  filter,
  setFilter,
  showCounts = false,
  searchTerm,
  onSearchChange,
  selectedPriorities,
  onPrioritiesChange,
}: {
  showKanban: boolean;
  mobileSurface: "quadro" | "agenda";
  onMobileSurfaceChange: (value: "quadro" | "agenda") => void;
  filter: TaskFilter;
  setFilter: (value: TaskFilter) => void;
  showCounts?: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedPriorities: TaskPriority[];
  onPrioritiesChange: (value: TaskPriority[]) => void;
}) {
  const [showSearch, setShowSearch] = useState(() => Boolean(searchTerm.trim()));
  const { counts } = useTaskDashboard();

  const filterLabel = FILTERS.find((f) => f.value === filter)?.label ?? "Todas";
  const countFor = (value: TaskFilter): number | null => {
    if (!showCounts) return null;
    if (value === "today") return counts.filterToday;
    if (value === "overdue") return counts.filterOverdue;
    if (value === "upcoming") return counts.filterUpcoming;
    return null;
  };

  return (
    <div className="flex flex-col gap-1.5 md:hidden">
      <div className="flex items-center gap-1.5 min-w-0">
        {showKanban && (
          <Select
            value={mobileSurface}
            onValueChange={(v) => onMobileSurfaceChange(v as "quadro" | "agenda")}
          >
            <SelectTrigger className="h-9 w-[7.25rem] shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="quadro">Quadro</SelectItem>
              <SelectItem value="agenda">Agenda</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Select value={filter} onValueChange={(v) => setFilter(v as TaskFilter)}>
          <SelectTrigger className="h-9 flex-1 min-w-0">
            <SelectValue placeholder={filterLabel} />
          </SelectTrigger>
          <SelectContent>
            {FILTERS.map((f) => {
              const n = countFor(f.value);
              return (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                  {n != null && n > 0 ? ` (${n})` : ""}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <TaskPriorityFilterButton
          selectedPriorities={selectedPriorities}
          onChange={onPrioritiesChange}
        />
        <Button
          type="button"
          variant={showSearch || searchTerm ? "default" : "outline"}
          size="icon"
          className="h-9 w-9 shrink-0"
          aria-label="Buscar"
          aria-pressed={showSearch}
          onClick={() => setShowSearch((open) => !open)}
        >
          <Search className="h-4 w-4" />
        </Button>
      </div>
      {showSearch && (
        <KanbanSearchInput
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          className="w-full"
        />
      )}
    </div>
  );
}

function TaskToolbar({
  searchTerm,
  onSearchChange,
  sort,
  onSortChange,
  assigneeFilter,
  onAssigneeChange,
  members,
}: {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  sort: TaskSort;
  onSortChange: (value: TaskSort) => void;
  assigneeFilter?: string | null;
  onAssigneeChange?: (value: string | null) => void;
  members?: WorkspaceMemberOption[];
}) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center shrink-0">
      <div className="flex gap-2 items-center">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por título..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0 md:hidden"
              aria-label="Ordenar e filtrar"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Ordenar por</DropdownMenuLabel>
            {SORT_OPTIONS.map((opt) => (
              <DropdownMenuItem key={opt.value} onClick={() => onSortChange(opt.value)}>
                {opt.label}
                {sort === opt.value ? " ·" : ""}
              </DropdownMenuItem>
            ))}
            {onAssigneeChange && members && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Responsável</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onAssigneeChange(null)}>Todos</DropdownMenuItem>
                {members.map((m) => (
                  <DropdownMenuItem key={m.id} onClick={() => onAssigneeChange(m.id)}>
                    {m.display_name ?? "Sem nome"}
                  </DropdownMenuItem>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {onAssigneeChange && members && (
        <Select
          value={assigneeFilter ?? "all"}
          onValueChange={(v) => onAssigneeChange(v === "all" ? null : v)}
        >
          <SelectTrigger className="hidden md:flex w-full sm:w-[180px] h-9">
            <SelectValue placeholder="Responsável" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os membros</SelectItem>
            {members.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.display_name ?? "Sem nome"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Select value={sort} onValueChange={(v) => onSortChange(v as TaskSort)}>
        <SelectTrigger className="hidden md:flex w-full sm:w-[160px] h-9">
          <SelectValue placeholder="Ordenar por" />
        </SelectTrigger>
        <SelectContent>
          {SORT_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function LoadMoreButton({
  hasMore,
  loadingMore,
  onLoadMore,
}: {
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}) {
  if (!hasMore) return null;
  return (
    <div className="flex justify-center pt-2">
      <Button variant="outline" size="sm" disabled={loadingMore} onClick={() => void onLoadMore()}>
        {loadingMore ? "Carregando..." : "Carregar mais"}
      </Button>
    </div>
  );
}

function KanbanSearchInput({
  searchTerm,
  onSearchChange,
  className,
}: {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  className?: string;
}) {
  return (
    <div className={cn("relative shrink-0", className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        placeholder="Buscar no board..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        className="pl-9 h-9"
      />
      {searchTerm && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
          onClick={() => onSearchChange("")}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function FilterBar({
  filter,
  setFilter,
  showCounts = false,
}: {
  filter: TaskFilter;
  setFilter: (f: TaskFilter) => void;
  showCounts?: boolean;
}) {
  const { counts } = useTaskDashboard();

  const countFor = (value: TaskFilter): number | null => {
    if (!showCounts) return null;
    switch (value) {
      case "today":
        return counts.filterToday;
      case "overdue":
        return counts.filterOverdue;
      case "upcoming":
        return counts.filterUpcoming;
      default:
        return null;
    }
  };

  return (
    <div className="flex gap-2 overflow-x-auto flex-nowrap snap-x snap-mandatory min-w-0 flex-1 pb-0.5 -mx-0.5 px-0.5">
      {FILTERS.map((f) => {
        const n = countFor(f.value);
        const active = filter === f.value;
        return (
          <Button
            key={f.value}
            size="sm"
            variant={active ? "default" : "outline"}
            onClick={() => setFilter(f.value)}
            className="snap-start shrink-0 rounded-full h-8 px-3.5"
          >
            {f.label}
            {n != null && n > 0 ? (
              <span className="ml-1 tabular-nums opacity-80">{n}</span>
            ) : null}
          </Button>
        );
      })}
    </div>
  );
}
